import { inject, observer } from 'mobx-react';
import React from 'react';
import { Table, Icon, Text, TableCell, TableRow, Spinner, TooltipToggle, Tooltip } from 'telia-front-react';
import Ms365Store, { Ms365Price } from '../stores/Ms365Store';
import { TranslationsContext } from '@teliaee/telia-translate';
import { FetchStatus } from '../util/FetchStatus';
import { replaceDotsAndFormatToTwoDecimalPlacesRoundingUp } from '../util/PriceUtil';
import { FEATURE_PACKAGES_MAPPING, PACKAGE_CODES } from './FeaturePackagesMapping';

export interface Ms3655PackageComparisonProps {
  maxHeight?: number;
  ms365Store?: Ms365Store;
}

@observer
class Ms365PackageComparison extends React.Component<Ms3655PackageComparisonProps> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;
  public componentDidMount() {
    this.props.ms365Store.fetchPrices();
  }

  private renderDiscountPrice(priceData: Ms365Price) {
    const translate = this.context.translate;

    const discountPrice = replaceDotsAndFormatToTwoDecimalPlacesRoundingUp(priceData?.discountPrice);
    const regularPrice = replaceDotsAndFormatToTwoDecimalPlacesRoundingUp(priceData?.price);

    return (
      <div className="text-light">
        <div>
          <strong className="h6">
            {translate('microsoft-365.compare-packages.price', {
              price: discountPrice,
            })}
          </strong>
        </div>
        <div>
          <Tooltip
            referenceElement={
              <TooltipToggle id="ms365-discount-tooltip">
                {translate('microsoft-365.compare-packages.regular-price', {
                  price: regularPrice,
                })}
              </TooltipToggle>
            }
          >
            {priceData?.discountEndDate
              ? `${translate('microsoft-365.compare-packages.discount.valid-until')} ${priceData?.discountEndDate}`
              : translate('microsoft-365.compare-packages.valid-permanent')}
          </Tooltip>
        </div>
      </div>
    );
  }

  private renderPrice(packageCode: string) {
    const translate = this.context.translate;
    const priceData = this.props.ms365Store.prices.get(packageCode);
    const price = replaceDotsAndFormatToTwoDecimalPlacesRoundingUp(priceData?.price);

    return priceData.isDiscount ? (
      this.renderDiscountPrice(priceData)
    ) : (
      <div className="text-light">
        <div>
          <strong className="h6">{translate('microsoft-365.compare-packages.price', { price })}</strong>
        </div>
      </div>
    );
  }

  private renderTableHead() {
    const translate = this.context.translate;

    return (
      <TableRow type={'header'} key={'packages-row'}>
        <TableCell width={6} type="th" key={'features-label-col'} />
        {PACKAGE_CODES.map((packageCode) => (
          <TableCell width={2} type="th" key={`package-${packageCode}-col`}>
            {translate(`microsoft-365.compare-packages.${packageCode}.label`)}
          </TableCell>
        ))}
      </TableRow>
    );
  }

  private renderPackageFeatures(): React.ReactElement {
    const translate = this.context.translate;
    const { ms365Store } = this.props;
    const shouldShowPrice = Array.from(this.props.ms365Store.prices.values()).every((p) => !!p?.price);

    return (
      <>
        {ms365Store.status !== FetchStatus.ERROR && shouldShowPrice && (
          <TableRow key={'feature-price-row'}>
            <TableCell className="text-bold" key={'feature-price-col'}>
              {translate('microsoft-365.compare-packages.monthly-price')}
            </TableCell>
            {PACKAGE_CODES.map((packageCode) => (
              <TableCell key={`feature-price-${packageCode}-col`}>{this.renderPrice(packageCode)}</TableCell>
            ))}
          </TableRow>
        )}
        {FEATURE_PACKAGES_MAPPING.map((mapping) => (
          <TableRow key={`feature-${mapping.featureLabel}-row`}>
            <TableCell key={`feature-${mapping.featureLabel}-col`}>{translate(mapping.featureLabel)}</TableCell>
            {PACKAGE_CODES.map((packageCode) => (
              <TableCell key={`feature-${mapping.featureLabel}-${packageCode}-col`}>{mapping.mappings[packageCode] && <Icon modifiers="#check" />}</TableCell>
            ))}
          </TableRow>
        ))}
        <TableRow key={'feature-max-count-col'}>
          <TableCell key={'feature-max-count-col'}>{translate('microsoft-365.compare-packages.max-users-count')}</TableCell>
          {PACKAGE_CODES.map((packageCode) => (
            <TableCell key={`feature-max-count-${packageCode}-col`}>
              <Text className="text-bold">300</Text>
            </TableCell>
          ))}
        </TableRow>
      </>
    );
  }

  public render() {
    const { ms365Store, maxHeight } = this.props;

    if ([FetchStatus.FETCHING, FetchStatus.UNINITIALIZED].includes(ms365Store.status)) {
      return <Spinner />;
    }
    return (
      <Table stickyHeader="level-1" maxHeight={maxHeight}>
        <thead>{this.renderTableHead()}</thead>
        <tbody>{this.renderPackageFeatures()}</tbody>
      </Table>
    );
  }
}

export default inject('ms365Store')(Ms365PackageComparison);
